import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, ImageOff, Loader2, MapPin, Mail, Phone } from "lucide-react";
import { http, divisionOf, formatPrice, imageUrl, telHref, SOURCES } from "../lib/api";
import { useSettings } from "../lib/settings";
import { StatusBadge, VerifiedBadge } from "../components/site/AssetCard";
import { InquiryForm } from "../components/site/InquiryForm";
import { ShareListing } from "../components/site/ShareListing";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const Gallery = ({ images, title }) => {
  const [i, setI] = useState(0);
  if (!images.length)
    return <div className="aspect-[16/10] grid place-items-center bg-[var(--ink-2)] border hairline text-slate-500"><ImageOff className="w-10 h-10" /></div>;
  const go = (d) => setI((i + d + images.length) % images.length);
  return (
    <div data-testid="asset-gallery">
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--ink-2)] border hairline group">
        {images.map((src, idx) => (
          <motion.img key={src} src={imageUrl(src)} alt={`${title} photo ${idx + 1}`} initial={false}
            animate={{ opacity: idx === i ? 1 : 0, scale: idx === i ? 1 : 1.04 }} transition={{ duration: 0.6 }}
            className="absolute inset-0 w-full h-full object-cover" />
        ))}
        {images.length > 1 && (
          <>
            <button onClick={() => go(-1)} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-[#0a0d14]/70 backdrop-blur border border-white/20 hover:border-[var(--gold)] transition-colors" data-testid="gallery-prev-button" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => go(1)} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-[#0a0d14]/70 backdrop-blur border border-white/20 hover:border-[var(--gold)] transition-colors" data-testid="gallery-next-button" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
            <span className="absolute bottom-4 right-4 font-mono text-xs bg-[#0a0d14]/70 px-3 py-1">{i + 1} / {images.length}</span>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 mt-2">
          {images.map((src, idx) => (
            <button key={src} onClick={() => setI(idx)} className={`aspect-[4/3] overflow-hidden border transition-[border-color,opacity] ${idx === i ? "border-[var(--gold)]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`gallery-thumb-${idx}`}>
              <img src={imageUrl(src)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AssetDetail() {
  const { id } = useParams();
  const s = useSettings();
  const [open, setOpen] = useState(false);
  const { data: a, isLoading, isError } = useQuery({ queryKey: ["asset", id], queryFn: async () => (await http.get(`/assets/${id}`)).data, retry: 0 });

  if (isLoading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (isError || !a)
    return (
      <div className="min-h-[70vh] pt-40 max-w-3xl mx-auto px-5 text-center" data-testid="asset-not-found">
        <h1 className="font-display text-5xl">Listing not available</h1>
        <p className="text-slate-300 mt-4">This listing may have been sold or removed.</p>
        <Link to="/inventory" className="btn-gold mt-8">Back to inventory</Link>
      </div>
    );

  const div = divisionOf(a.division);
  const sold = a.status === "sold";
  const facts = [["Division", div?.name], ["Asset type", a.category], ["Make", a.make], ["Model", a.model], ["Year", a.year], ["Condition", a.condition],
    [div?.usage || "Usage", a.usage], ["Location", a.location], ["Ownership", a.source && a.source !== "unspecified" ? SOURCES[a.source] : ""], ["Stock #", a.stock_number]].filter(([, v]) => v);

  return (
    <div className="pt-28 md:pt-32 pb-24" data-testid="asset-detail-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Link to={`/divisions/${a.division}`} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold mb-8" data-testid="asset-back-link"><ArrowLeft className="w-4 h-4" /> {div?.name} inventory</Link>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7 space-y-12">
            <Gallery images={a.images || []} title={a.title} />
            {a.description && (
              <section>
                <p className="eyebrow mb-4">Description</p>
                <p className="text-slate-200 leading-relaxed whitespace-pre-line" data-testid="asset-description">{a.description}</p>
              </section>
            )}
            {a.specs?.length > 0 && (
              <section>
                <p className="eyebrow mb-4">Specifications</p>
                <dl className="grid sm:grid-cols-2 border-t hairline" data-testid="asset-specs">
                  {a.specs.map((sp, i) => (
                    <div key={i} className="flex justify-between gap-4 py-3.5 border-b hairline sm:odd:pr-6 sm:even:pl-6">
                      <dt className="text-sm text-slate-400">{sp.label}</dt><dd className="text-sm text-slate-100 text-right">{sp.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="flex flex-wrap gap-2"><StatusBadge status={a.status} /><VerifiedBadge asset={a} /></div>
              {a.verified && <p className="text-xs text-slate-400 -mt-3" data-testid="asset-verified-note">Listing details and photos were reviewed by Selby Asset Group before publishing.</p>}<h1 className="font-display text-4xl md:text-5xl leading-[1.02] text-slate-50" data-testid="asset-title">{a.title}</h1>
              {a.location && <p className="flex items-center gap-2 text-sm text-slate-300"><MapPin className="w-4 h-4 text-gold" /> {a.location}</p>}
              <p className="font-display text-4xl text-gold" data-testid="asset-price">{sold ? "Sold" : formatPrice(a.price)}</p>
              <dl className="border hairline divide-y divide-white/10 bg-[var(--ink-2)]">
                {facts.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 px-5 py-3"><dt className="text-sm text-slate-400">{k}</dt><dd className={`text-sm text-slate-100 text-right ${k === "Stock #" ? "font-mono" : ""}`}>{v}</dd></div>
                ))}
              </dl>
              {!sold && (
                <button onClick={() => setOpen(true)} className="btn-gold w-full !py-4" data-testid="asset-inquire-button">
                  {a.price === null || a.price === undefined ? "Request Price" : "Inquire About This Asset"}
                </button>
              )}
              <div className="flex flex-col sm:flex-row gap-3 text-sm">
                <a href={telHref(s.phone)} className="btn-ghost flex-1 !py-3" data-testid="asset-call-link"><Phone className="w-4 h-4" /> {s.phone}</a>
                <a href={`mailto:${s.sales_email}?subject=${encodeURIComponent(`Inquiry: ${a.title} (${a.stock_number})`)}`} className="btn-ghost flex-1 !py-3" data-testid="asset-email-link"><Mail className="w-4 h-4" /> Email Sales</a>
              </div>
              <ShareListing asset={a} />
              <p className="text-xs text-slate-400 leading-relaxed">
                Listing information is provided as available and may change, and assets are subject to prior sale. Verification means {s.company_name} reviewed the listing details and photos; it is not a mechanical or independent inspection. Please inspect and verify all details before purchase.
              </p>
            </div>
          </aside>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[var(--ink-2)] border-white/10 rounded-none max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="inquiry-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-medium">Asset inquiry</DialogTitle>
            <DialogDescription className="text-slate-300">We'll respond with availability, details and next steps.</DialogDescription>
          </DialogHeader>
          <InquiryForm asset={a} testid="asset-inquiry-form" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
